<template>
  <div :data-cy="`game-item-${value.id}`" class="games-list-item-component">
    <router-link :to="link">
      <section class="actions">
        <VdButton
          v-if="canDeleteGames"
          type="tertiary"
          icon="fas:trash"
          skin="white"
          color="var(--vd-color-white)"
          class="delete-btn"
          size="large"
          @click.prevent="onDeleteApp()"
        ></VdButton>
      </section>
      <el-image :src="value.iconUrl || placeholder" fit="cover" class="game-image" />
      <span class="game-list-item-name">
        {{ value.name }}
      </span>
    </router-link>
  </div>
</template>

<script lang="ts">
/**
 * This component display a game for the GamesListPage component.
 * It displayed the game icon and its name.
 * If we click on the component, we are redirect to the game page.
 */
import { VdButton } from "@voodoo.io/voodoo-ui";
import { defineComponent, PropType } from "vue";
import { RouteLocationRaw } from "vue-router";

import { queryNames, useMutationResponse, GamesListPageGameFragment, useDeleteAppMutation } from "@/graphql";
import useMessages from "@/hooks/messages";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

import placeholder from "../assets/placeholder.svg";

export default defineComponent({
  name: "GamesListItem",
  components: {
    VdButton,
  },

  props: {
    /**
     * The game value passed to the component.
     * The used attributed are iconUrl, name and id.
     */
    value: {
      type: Object as PropType<GamesListPageGameFragment>,
      required: true,
    },
  },

  setup(props) {
    const { canDeleteGames } = usePermissions();
    const { confirm } = useMessages();
    const { mutate: deleteApp } = useDeleteAppMutation({ refetchQueries: [queryNames.GamesListPage] });

    /**
     * onSubmit will be called when the form is submitted.
     * If the form is valid, it will post the data to the server
     * and redirect to the games list.
     */

    const onDeleteApp = async () => {
      const confirmed = await confirm("Do you really want to delete this game?");
      if (confirmed) {
        const response = await deleteApp({ unifiedAppId: props.value.id });
        const deleteAppResult = useMutationResponse(response, (resp) => resp?.deleteApp);
        if (deleteAppResult?.success) {
          console.log("Success!");
        }
      }
    };

    /**
     * The game link is targeting the wip configuration version as
     *  we don't known which versions are available yet.
     */
    const link: RouteLocationRaw = {
      name: links.games.home,
      params: {
        gameId: props.value.id,
        versionId: "wip",
      },
    };

    return {
      link,
      placeholder,
      canDeleteGames,
      onDeleteApp,
    };
  },
});
</script>

<style lang="scss" scoped>
.games-list-item-component {
  @extend %card;
  overflow: hidden;
  position: relative;
  width: 180px;
  height: 180px;
  border-radius: 40px;
  border: 0.5px solid rgba($color: #000000, $alpha: 0.2);
  text-shadow: 1px 1px rgba($color: #000000, $alpha: 0.5);

  > a {
    text-decoration: none;
    color: $foggy-blue;

    &:visited {
      color: $foggy-blue;
    }
  }

  &:hover {
    .game-image {
      filter: brightness(0.7);
    }

    .actions {
      visibility: visible;
      opacity: 1;
      transition: visibility 0s linear 0s, opacity 300ms;
    }
  }

  .actions {
    visibility: hidden;
    opacity: 0;
    transition: visibility 0s linear 300ms, opacity 300ms;
    position: absolute;
    top: 0;
    height: 33%;
    width: 100%;

    .delete-btn {
      position: absolute;
      top: 20%;
      right: 10%;
      z-index: 1000;

      :deep(svg) {
        color: var(--vd-color-white) !important;
        box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.5);
      }
    }
  }

  .game-list-item-name {
    display: block;
    width: 100%;
    font-size: font("ML");
    font-weight: 600;
    background: linear-gradient(transparent, rgba($color: #000000, $alpha: 0.5));
    position: absolute;
    bottom: 0;
    z-index: 1;
    text-align: center;
    padding: size("XS") 0;
  }

  .game-image {
    display: inline-block;
    object-fit: cover;
    width: 100%;
    max-height: 100%;
    transition: filter 0.3s ease-in;
  }
}
</style>
